"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ANALYTICS_EVENTS, track } from "@/lib/analytics";
import { resolveBg } from "@/lib/bg-color";
import { youtubeThumbnail } from "@/lib/youtube";
import YouTubePlayer from "@/components/video/youtube-player";

type VideoItem = SbBlokData & {
  youtube_url?: string;
  quote?: string;
  name?: string;
};

type VideoTestimonialsBlok = SbBlokData & {
  title?: string;
  description?: string;
  videos?: VideoItem[];
  view_more_label?: string;
  bg_color?: unknown;
};

// Collapsed grid height — taller on desktop. Keep in sync with the `max-h-*`
// classes on the clip wrapper below; the lg breakpoint is 1115px. Matched to
// the Reviews grid so both modules collapse to the same height.
const COLLAPSED_MAX_H_MOBILE = 1000;
const COLLAPSED_MAX_H_DESKTOP = 1700;

function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function asQuote(text: string): string {
  const inner = text
    .trim()
    .replace(/^["'‘’“”]+|["'‘’“”]+$/g, "")
    .trim();
  return `‘${inner}’`;
}

function PlayButton({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 68 48" className={className} aria-hidden>
      <path
        fill="#ff0000"
        d="M66.5 7.5c-.8-3-3.2-5.4-6.2-6.2C54.9 0 34 0 34 0S13.1 0 7.7 1.3C4.7 2.1 2.3 4.5 1.5 7.5 0 13 0 24 0 24s0 11 1.5 16.5c.8 3 3.2 5.4 6.2 6.2C13.1 48 34 48 34 48s20.9 0 26.3-1.3c3-.8 5.4-3.2 6.2-6.2C68 35 68 24 68 24s0-11-1.5-16.5z"
      />
      <path fill="#fff" d="M27 34V14l17 10z" />
    </svg>
  );
}

// Only one card plays at a time and the rest dim while it does.
function VideoCard({
  video,
  onDark,
  isPlaying,
  dimmed,
  inline,
  onPlay,
  onState,
  posterRef,
  posterTabIndex,
  onPosterKeyDown,
  onPosterFocus,
  cardInert,
}: {
  video: VideoItem;
  onDark: boolean;
  isPlaying: boolean;
  dimmed: boolean;
  inline: boolean;
  onPlay: (uid: string) => void;
  onState: (uid: string, state: number) => void;
  posterRef?: (el: HTMLButtonElement | null) => void;
  posterTabIndex?: number;
  onPosterKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onPosterFocus?: () => void;
  cardInert?: boolean;
}) {
  const id = extractYouTubeId(video.youtube_url);
  const uid = video._uid as string;

  const handleStateChange = useCallback(
    (state: "playing" | "paused" | "ended") => {
      // Map Plyr states to YouTube IFrame API state codes the parent expects:
      // 0 = ended, 1 = playing, 2 = paused.
      const code = state === "playing" ? 1 : state === "paused" ? 2 : 0;
      onState(uid, code);
    },
    [onState, uid],
  );

  return (
    <div
      {...storyblokEditable(video)}
      data-video-card
      inert={cardInert}
      className={`flex flex-col gap-4 transition-opacity duration-300 ${
        dimmed ? "opacity-40 hover:opacity-100" : "opacity-100"
      }`}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-[12px] bg-black">
        {isPlaying && id ? (
          <div className="absolute inset-0 size-full">
            <YouTubePlayer
              videoId={id}
              title={video.name}
              inline={inline}
              autoStart
              onStateChange={handleStateChange}
            />
          </div>
        ) : id ? (
          <button
            ref={posterRef}
            type="button"
            tabIndex={posterTabIndex}
            onKeyDown={onPosterKeyDown}
            onFocus={onPosterFocus}
            onClick={() => {
              track(ANALYTICS_EVENTS.VIDEO_PLAYED, {
                module: "Video Testimonials",
                video: video.name,
              });
              onPlay(uid);
            }}
            aria-label={`Play video${video.name ? ` — ${video.name}` : ""}`}
            className="video-poster-btn group absolute inset-0 size-full cursor-pointer"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={youtubeThumbnail(id)}
              alt=""
              loading="lazy"
              className="absolute inset-0 size-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/5">
              <PlayButton className="h-[40px] w-[56px] drop-shadow-lg transition-transform group-hover:scale-105" />
            </span>
          </button>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2">
        {video.quote && (
          <blockquote
            className={`text-body leading-normal italic ${
              onDark ? "text-white/85" : "text-black/80"
            }`}
          >
            {asQuote(video.quote)}
          </blockquote>
        )}
        {video.name && (
          <p className={`text-body mt-auto font-bold ${onDark ? "text-white" : "text-black"}`}>
            {video.name}
          </p>
        )}
      </div>
    </div>
  );
}

export default function VideoTestimonials({ blok }: { blok: VideoTestimonialsBlok }) {
  const { bg, onDark, hex } = resolveBg(blok.bg_color, "white");
  const videos = useMemo(() => blok.videos ?? [], [blok.videos]);

  const [playingUid, setPlayingUid] = useState<string | null>(null);
  const [activeUid, setActiveUid] = useState<string | null>(null);
  const [inline, setInline] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const [overflows, setOverflows] = useState(true);
  // How many videos sit above the collapsed fold — only those are reachable by
  // keyboard until "View more" is clicked, so tabbing/arrowing never reveals
  // clipped content.
  const [aboveFoldCount, setAboveFoldCount] = useState(videos.length);
  // Roving tabindex: the grid is one tab stop; arrows move between videos.
  const [activeIndex, setActiveIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const posterRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());


  useEffect(() => {
    if (!playingUid) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.fullscreenElement) return;
      const uid = playingUid;
      setPlayingUid(null);
      setActiveUid(null);
      requestAnimationFrame(() => posterRefs.current.get(uid)?.focus());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playingUid]);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const check = () => {
      const limit = window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;
      setOverflows(el.offsetHeight > limit + 8);
      const gridTop = el.getBoundingClientRect().top;
      const cards = Array.from(
        el.querySelectorAll<HTMLElement>("[data-video-card]"),
      );
      const above = cards.filter(
        (c) => c.getBoundingClientRect().top - gridTop < limit - 8,
      ).length;
      setAboveFoldCount(above || cards.length);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [videos.length]);

  const collapsed = !expanded && overflows;
  const visibleCount = collapsed ? aboveFoldCount : videos.length;
  const roveIndex = Math.min(activeIndex, Math.max(0, visibleCount - 1));

  // Arrows move between the videos that are actually reachable (above the fold
  // when collapsed); clamped at the ends so they never jump into clipped ones.
  const handlePosterKey = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const total = visibleCount;
      if (total === 0) return;
      let next = index;
      if (e.key === "ArrowRight" || e.key === "ArrowDown")
        next = Math.min(index + 1, total - 1);
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
        next = Math.max(index - 1, 0);
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = total - 1;
      else return;
      e.preventDefault();
      setActiveIndex(next);
      const uid = videos[next]?._uid as string | undefined;
      if (uid) posterRefs.current.get(uid)?.focus();
    },
    [visibleCount, videos],
  );

  const handleViewMore = useCallback(() => {
    const grid = gridRef.current;
    const limit =
      window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;
 
    flushSync(() => setExpanded(true));
    if (!grid) return;
    const gridTop = grid.getBoundingClientRect().top;
    const buttons = Array.from(
      grid.querySelectorAll<HTMLElement>(".video-poster-btn"),
    );
    const firstBelow = buttons.find(
      (btn) => btn.getBoundingClientRect().top - gridTop >= limit - 8,
    );
    (firstBelow ?? buttons[0])?.focus();
  }, []);

  const handlePlay = useCallback((uid: string) => {
    setInline(window.matchMedia("(min-width: 768px)").matches);
    setPlayingUid(uid);
    setActiveUid(uid);
  }, []);

  // 1 = playing, 2 = paused, 0 = ended. Pausing or finishing lifts the dim.
  const handleState = useCallback((uid: string, state: number) => {
    if (state === 1) {
      setActiveUid(uid);
    } else if (state === 0 || state === 2) {
      setActiveUid((prev) => (prev === uid ? null : prev));
    }
  }, []);

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4.5rem)]">
        <div className="flex flex-col gap-4">
          {blok.title && (
            <h2
              className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                onDark ? "text-white" : "text-purple-05"
              }`}
            >
              {blok.title}
            </h2>
          )}
          {blok.description && (
            <p
              className={`text-body max-w-[640px] leading-[1.6] ${
                onDark ? "text-white/85" : "text-black/65"
              }`}
            >
              {blok.description}
            </p>
          )}
        </div>

        {videos.length > 0 && (
          <div className="relative mt-[clamp(2rem,4vw,3.5rem)]">
            <div className={collapsed ? "max-h-[1000px] overflow-hidden lg:max-h-[1700px]" : ""}>
              <div
                ref={gridRef}
                className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3"
              >
                {videos.map((video, index) => {
                  const uid = video._uid as string;
                  return (
                    <VideoCard
                      key={uid}
                      video={video}
                      onDark={onDark}
                      isPlaying={playingUid === uid}
                      dimmed={activeUid !== null && activeUid !== uid}
                      inline={inline}
                      onPlay={handlePlay}
                      onState={handleState}
                      posterRef={(el) => posterRefs.current.set(uid, el)}
                      posterTabIndex={index === roveIndex ? 0 : -1}
                      onPosterKeyDown={(e) => handlePosterKey(e, index)}
                      onPosterFocus={() => setActiveIndex(index)}
                      cardInert={collapsed && index >= aboveFoldCount}
                    />
                  );
                })}
              </div>
            </div>
            {collapsed && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
                style={{ background: `linear-gradient(to top, ${hex}, transparent)` }}
              />
            )}
          </div>
        )}

        {collapsed && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={handleViewMore}
              className={`text-body inline-flex cursor-pointer items-center gap-1.5 font-bold hover:underline ${
                onDark ? "text-white" : "text-purple-03"
              }`}
            >
              {blok.view_more_label || "View more"}
              <span aria-hidden className="text-body leading-none">
                +
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
